import { useState } from "react";
import type { CatalogIndex, CatalogItemView } from "../../data/local/catalog";
import { needsConfiguration } from "../../data/local/catalog";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { Slot } from "../slots/Slot";

/**
 * The menu grid.
 *
 * One tap adds an item that has no genuine choice to make (FR-008). An item
 * with sizes or required modifiers opens a sheet instead. The distinction is
 * `needsConfiguration`, and it is the single biggest lever on how fast the till
 * feels: interrupting for a choice that does not exist is what makes most POS
 * software tedious.
 *
 * Two levels of navigation: a row of tabs for top-level categories, and inside
 * the active tab, sticky headings for its child categories. Which categories
 * exist is entirely the catalog's business — this component reads the tree and
 * renders it, and knows nothing about what is being sold.
 *
 * A tab with no children of its own renders its items directly, with no
 * heading — a lone heading repeating the tab name above it is noise.
 */

interface MenuGridProps {
  index: CatalogIndex;
  currency: CurrencyFormat;
  onAdd(view: CatalogItemView): void;
  onConfigure(view: CatalogItemView): void;
}

export function MenuGrid({ index, currency, onAdd, onConfigure }: MenuGridProps) {
  const [chosenTab, setChosenTab] = useState<string | null>(null);

  /**
   * DERIVED, not stored.
   *
   * The catalog arrives asynchronously — the till renders from the local
   * replica first and re-renders when the refresh lands. A `useState`
   * initialiser runs once, so seeding it from `tabs[0]` captured an empty list
   * and every subsequent lookup missed, leaving the grid permanently empty.
   * Falling back whenever the chosen id is absent makes that impossible.
   */
  const activeTab =
    chosenTab && index.tabs.some((t) => t.id === chosenTab) ? chosenTab : (index.tabs[0]?.id ?? "");

  /*
   * A tab's own items come first, then each of its sections. A tab normally
   * has one or the other, but nothing stops it having both, and silently
   * dropping directly-attached items would make them unsellable.
   */
  const ownItems = index.itemsByCategory.get(activeTab) ?? [];
  const sections = [
    ...(ownItems.length > 0 ? [{ category: null, items: ownItems }] : []),
    ...(index.sectionsByTab.get(activeTab) ?? []).map((category) => ({
      category,
      items: index.itemsByCategory.get(category.id) ?? [],
    })),
    // An empty section renders nothing at all — a bare heading over blank
    // space reads as a loading failure rather than as an empty section.
  ].filter((section) => section.items.length > 0);

  return (
    <div className="menu">
      {index.tabs.length > 1 && (
        <nav className="menu__tabs" aria-label="Menu">
          {index.tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`menu__tab${tab.id === activeTab ? " is-active" : ""}`}
              onClick={() => setChosenTab(tab.id)}
              aria-pressed={tab.id === activeTab}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      )}

      <div className="menu__scroll">
        {sections.map(({ category, items }) => (
          <section
            key={category?.id ?? "__own"}
            className="menu__section"
            aria-labelledby={category ? `menu-section-${category.id}` : undefined}
          >
            {category && (
              <h2 className="menu__section-title" id={`menu-section-${category.id}`}>
                {category.name}
              </h2>
            )}

            {/*
              No role="list" here, and no role="listitem" on the tiles.
              An explicit role on a <button> REPLACES its implicit one, so tiles
              were being announced as list items rather than buttons — which
              tells a screen reader user they cannot press them. A grid of
              buttons needs no ARIA to be understood.
            */}
            <div className="menu__grid">
              {items.map((view) => {
                const price = view.defaultVariant?.price ?? 0;
                const unavailable = !view.item.available;

                return (
                  <button
                    key={view.item.id}
                    type="button"
                    className={`menu__tile${unavailable ? " is-unavailable" : ""}`}
                    disabled={unavailable}
                    onClick={() => (needsConfiguration(view) ? onConfigure(view) : onAdd(view))}
                  >
                    <span className="menu__tile-name">{view.item.name}</span>

                    <span className="menu__tile-meta">
                      <span className="menu__tile-price money">
                        {formatMoney(price as never, currency)}
                      </span>
                      {/* Modules render here — nutrition puts calories on the
                          tile. With every module off this renders nothing. */}
                      <Slot id="till.item.badge" itemId={view.item.id} />
                    </span>

                    {/*
                      Always rendered, even with nothing to say. A tile that
                      omits this line lets `space-between` pull its price down
                      to the tile floor, so a single-variant item sits a line
                      below its neighbours and a row of prices reads ragged.
                      Reserving the line keeps every price on one baseline.
                    */}
                    {unavailable ? (
                      <span className="menu__tile-sold">Sold out</span>
                    ) : (
                      <span className="menu__tile-hint">
                        {view.variants.length > 1 ? `${view.variants.length} sizes` : ""}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        {sections.length === 0 && (
          <p className="menu__empty">
            {index.categories.length === 0
              ? "No menu on this terminal yet. Connect once to load it."
              : "Nothing in this section yet."}
          </p>
        )}
      </div>
    </div>
  );
}

export default MenuGrid;
